package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

val QaPageStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(1160.px)
        .gap(Space.XXL)
        .styleModifier { property("margin", "0 auto") }
}

val QaFilterBarStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            .padding(Space.LG)
            .gap(Space.LG)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "stretch")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("align-items", "flex-end")
            property("justify-content", "space-between")
        }
    }
}

val QaFilterControlsStyle = CssStyle {
    base {
        Modifier
            .gap(Space.LG)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "stretch")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("align-items", "flex-end")
        }
    }
}

val QaLocaleSelectStyle = CssStyle {
    base { Modifier.fillMaxWidth() }
    Breakpoint.MD { Modifier.width(260.px) }
}

val QaSummaryStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("flex-wrap", "wrap")
        }
}

val QaRefreshStatusStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
        .styleModifier { property("min-height", "20px") }
}

val QaGroupStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toSitePalette().surface)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Card)
        .styleModifier {
            property("overflow", "hidden")
        }
}


val QaIssueRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .border(0.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .padding(topBottom = Space.LG, leftRight = Space.XL)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "18px minmax(0, 1fr)")
                property("align-items", "start")
                property("gap", "${Space.MD}")
                property("text-align", "start")
            }
            .transitionColors()
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            // `minmax(0, …)`, not `minmax(180px, …)`. Hard floors do not shrink: 18 + 180 + 240 +
            // a nowrap badge + 130, plus four 12px gaps and 24px of padding either side, needs more
            // room than this grid gets at the width that switches it on — so the row overflowed
            // sideways and took the page's horizontal scrollbar with it. The proportions are what
            // matter here; the floors were only ever a guess at them.
            property("grid-template-columns", "18px minmax(0, .85fr) minmax(0, 1.4fr) auto minmax(0, 130px)")
            property("align-items", "center")
        }
    }
    cssRule(":last-child") {
        Modifier.borderBottom(0.px, LineStyle.Solid, colorMode.toSitePalette().border)
    }
    cssRule(":hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceRaised)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "-2px") }
    }
}

val QaSeverityDotStyle = CssStyle.base {
    Modifier
        .width(9.px)
        .styleModifier {
            property("display", "inline-block")
            property("height", "9px")
            property("flex", "0 0 auto")
            property("border-radius", "${Radius.Pill}")
            property("margin-top", "5px")
            property("box-shadow", "0 0 0 3px currentColor")
            property("opacity", ".88")
        }
}

val QaKeyStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(600)
        .lineHeight(TypeScale.BodyLineHeight)
        .styleModifier {
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
        }
}

val QaMessageStyle = CssStyle {
    base {
        Modifier
            .minWidth(0.px)
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.BodySmall)
            .lineHeight(TypeScale.BodyLineHeight)
            .styleModifier {
                property("grid-column", "2")
                // **`min-width: 0` is not enough on its own.** It lets the track shrink, but the
                // text inside still cannot break, so one long word sets the box's minimum width and
                // pushes the row open anyway. A QA message quotes the translator's own string, and
                // German compounds do not offer a break opportunity —
                // "Rechtsschutzversicherungsgesellschaftsbeauftragte" measured 414px inside a 318px
                // row at 320px wide. Same treatment as the editor's own QA findings.
                property("overflow-wrap", "anywhere")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("grid-column", "auto") }
    }
}

val QaMobileMetaStyle = CssStyle {
    base {
        Modifier
            .gap(Space.SM)
            .padding(top = Space.SM)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("flex-wrap", "wrap")
                property("grid-column", "2")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("display", "none") }
    }
}

val QaDesktopCellStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "none") } }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
        }
    }
}

val QaOpenLabelStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .color(colorMode.toSitePalette().accent)
        .fontSize(TypeScale.Small)
        .fontWeight(600)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "flex-end")
            property("white-space", "nowrap")
        }
}

/**
 * The placeholder for [QaIssueRowStyle], and it has to be responsive for the same reason that one
 * is: as a single un-breakpointed grid with 180px and 240px floors it could not fit a phone, so the
 * loading state overflowed on exactly the screens the real rows were careful not to.
 */
val QaSkeletonRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = Space.LG, leftRight = Space.XL)
            .gap(Space.LG)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("align-items", "center")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("grid-template-columns", "minmax(0, .85fr) minmax(0, 1.4fr) minmax(0, 100px)")
        }
    }
}
